import { NATIONAL_STATE_CODE } from 'db/schema/stateProductCounts';
import { SUPPORTED_PLAN_YEARS } from 'lib/planYears';
import {
  DEFAULT_NATIONAL_MED_SUP_COUNT,
  type StateProductCountEntry,
} from 'lib/stateProductCounts';

export interface ResolvedAvailabilityCounts {
  carrierCount: number | '';
  productCount: number | '';
  maOnlyCount: number | '';
  maPdCount: number | '';
  maSubCount: number | '';
  pdpCount: number | '';
  /** Nationwide Med Sup product count (same for every state). */
  medSupCount: number | '';
}

function normalizeStateCode(state?: string): string | undefined {
  const code = state?.trim().toUpperCase().slice(0, 2);
  return code || undefined;
}

function resolveStateProductCountEntry(
  byState: Record<string, StateProductCountEntry>,
  state?: string
): StateProductCountEntry | undefined {
  const code = normalizeStateCode(state);
  if (code && byState[code]) {
    return byState[code];
  }
  return byState[NATIONAL_STATE_CODE];
}

function resolveCarrierCount(
  carrierCountsByState: Record<string, number>,
  state?: string
): number | '' {
  const code = normalizeStateCode(state);
  if (code && carrierCountsByState[code] != null) {
    return carrierCountsByState[code];
  }
  if (carrierCountsByState[NATIONAL_STATE_CODE] != null) {
    return carrierCountsByState[NATIONAL_STATE_CODE];
  }
  return '';
}

function countOrEmpty(value: number | undefined): number | '' {
  return value == null ? '' : value;
}

function resolveNationalMedSupCount(
  byState: Record<string, StateProductCountEntry>
): number | '' {
  const national = byState[NATIONAL_STATE_CODE];
  if (!national) {
    // No product-count data at all → leave blank for content authors.
    if (Object.keys(byState).length === 0) return '';
    return DEFAULT_NATIONAL_MED_SUP_COUNT;
  }
  return national.medSup;
}

interface AvailabilityYearCounts {
  stateProductCounts: Record<string, unknown>;
  carrierCountsByState: Record<string, number>;
}

function yearHasProductCounts(
  entry: AvailabilityYearCounts | undefined
): boolean {
  return !!entry && Object.keys(entry.stateProductCounts).length > 0;
}

function yearHasCarrierCounts(
  entry: AvailabilityYearCounts | undefined
): boolean {
  return !!entry && Object.keys(entry.carrierCountsByState).length > 0;
}

/**
 * Disclaimer copy uses one plan year for every availability count so a
 * sentence does not mix years. Prefer the newest supported year that has
 * both product-count and carrier-count rows. A newer year with only one of
 * those does not replace an older complete year, or the missing counts
 * render blank. If no year has both, use the newest year that has either.
 * The viewer's selected shopping year does not change these numbers.
 * `{{planYear}}` stays the coverage-year label.
 */
export function resolveAvailabilityYearForDynamicCopy<
  T extends AvailabilityYearCounts
>(byYear: Record<number, T | undefined>): T | undefined {
  const years = [...SUPPORTED_PLAN_YEARS].sort((left, right) => right - left);
  const entries = years.map((year) => byYear[year]);
  const complete = entries.find(
    (entry) => yearHasProductCounts(entry) && yearHasCarrierCounts(entry)
  );
  if (complete) return complete;
  return entries.find(
    (entry) => yearHasProductCounts(entry) || yearHasCarrierCounts(entry)
  );
}

export function resolveAvailabilityCountsForLocation(
  stateProductCounts: Record<string, StateProductCountEntry>,
  carrierCountsByState: Record<string, number>,
  state?: string
): ResolvedAvailabilityCounts {
  const entry = resolveStateProductCountEntry(stateProductCounts, state);

  return {
    carrierCount: resolveCarrierCount(carrierCountsByState, state),
    productCount: countOrEmpty(entry?.total),
    maOnlyCount: countOrEmpty(entry?.maOnly),
    maPdCount: countOrEmpty(entry?.maPd),
    maSubCount: countOrEmpty(entry?.maSub),
    pdpCount: countOrEmpty(entry?.pdp),
    medSupCount: resolveNationalMedSupCount(stateProductCounts),
  };
}
